import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExportButtons } from "@/components/ExportButtons";
import { DateFilters, filterParams, previousMonthFilters } from "@/components/DateFilters";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";

const Stat = ({ label, value, sub, testId, accent }) => (
  <Card className="border-slate-200 shadow-sm">
    <CardContent className="p-4">
      <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-black tracking-tight ${accent || "text-slate-900"}`} data-testid={testId}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

export default function GSTR3B() {
  const [filters, setFilters] = useState(() => previousMonthFilters());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/reports/gstr3b`, { params: filterParams(filters) }).then((r) => setData(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => {
    load();
    window.addEventListener("sap-synced", load);
    return () => window.removeEventListener("sap-synced", load);
  }, [load]);

  if (!data) return <div className="animate-pulse text-slate-400 text-sm">Loading GSTR-3B summary...</div>;

  return (
    <div className="space-y-6" data-testid="gstr3b-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">GSTR-3B Summary</h1>
          <p className="text-sm text-slate-500 mt-1">Monthly GST summary return — output tax liability, eligible ITC and net payable (home state {data.home_state})</p>
        </div>
        <div className="flex items-center gap-3">
          <DateFilters filters={filters} setFilters={setFilters} testPrefix="gstr3b" />
          <ExportButtons report="gstr3b" />
        </div>
      </div>

      <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-800" data-testid="gstr3b-caveat">
        <span className="font-semibold">Estimate:</span> Output tax (3.1a) is a working figure derived from AR receivables at 18% tax-inclusive. Once live SAP billing tax rates are wired, exact rate-wise (5/12/18/28%) and exempt/zero-rated splits will replace this. Not for statutory filing as-is.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 fade-in-up">
        <Stat label="Output Tax (3.1a)" value={inrShort(data.outward.total)} sub={`${data.outward_count} outward invoices`} testId="gstr3b-output" />
        <Stat label="Eligible ITC (Table 4)" value={inrShort(data.inward_itc.total)} sub={`${data.inward_count} inward invoices`} testId="gstr3b-itc" accent="text-emerald-700" />
        <Stat label="RCM Payable (3.1d)" value={inrShort(data.rcm_liability?.total || 0)} sub={`${data.rcm_liability?.count || 0} reverse-charge documents · posting-date basis · cash only`} testId="gstr3b-rcm" accent="text-amber-700" />
        <Stat label="Net Tax Payable (Cash)" value={inrShort(data.cash_with_rcm?.total ?? data.cash_payable.total)} sub={`after ITC set-off${data.rcm_liability?.total ? ` · incl. RCM ${inrShort(data.rcm_liability.total)}` : ""}`} testId="gstr3b-cash" accent="text-red-700" />
        <Stat label="ITC Carried Forward" value={inrShort(data.itc_balance.total)} sub="unutilised credit" testId="gstr3b-itc-balance" accent="text-sky-700" />
      </div>

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-800">Tax Head Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Table data-testid="gstr3b-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Tax Head</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Output Tax (3.1a)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">RCM Payable (3.1d)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Eligible ITC (4A)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">of which RCM ITC (4A3)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Payable in Cash</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.heads.map((h) => (
                <TableRow key={h.head} className="border-b border-slate-200" data-testid={`gstr3b-row-${h.head.toLowerCase()}`}>
                  <TableCell className="font-semibold text-slate-800">{h.head}</TableCell>
                  <TableCell className="text-right text-slate-700">{inr(h.output)}</TableCell>
                  <TableCell className="text-right text-amber-700" data-testid={`gstr3b-rcm-${h.head.toLowerCase()}`}>{inr(h.rcm || 0)}</TableCell>
                  <TableCell className="text-right text-emerald-700">{inr(h.itc)}</TableCell>
                  <TableCell className="text-right text-emerald-600">{inr(h.rcm_itc || 0)}</TableCell>
                  <TableCell className="text-right font-bold text-slate-900">{inr(h.cash_with_rcm ?? h.cash)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 font-bold">
                <TableCell className="text-slate-900">Total</TableCell>
                <TableCell className="text-right text-slate-900">{inr(data.outward.total)}</TableCell>
                <TableCell className="text-right text-amber-700" data-testid="gstr3b-rcm-total">{inr(data.rcm_liability?.total || 0)}</TableCell>
                <TableCell className="text-right text-emerald-700">{inr(data.inward_itc.total)}</TableCell>
                <TableCell className="text-right text-emerald-600">{inr(data.rcm_itc?.total || 0)}</TableCell>
                <TableCell className="text-right text-red-700">{inr(data.cash_with_rcm?.total ?? data.cash_payable.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <p className="text-xs text-slate-400 mt-3">Set-off order: IGST credit → IGST, CGST, SGST · CGST credit → CGST, IGST · SGST credit → SGST, IGST. Output tax derived from AR receivables (18% inclusive); ITC from GSTR-2 eligible invoices. RCM payable (3.1d) comes from the credits on the RCM GST accounts (12605912/13/14) and must be paid in cash — it cannot be set off against ITC; the matching credit is shown under 4(A)(3).</p>
        </CardContent>
      </Card>
    </div>
  );
}
